import { FC } from 'react';

import { defectIsBinnenPeriodeNietInzetbaar, defectTypeNietInzetbaar, parseSnelheid } from '@/domain/defecten/defect';
import { Conflict } from '@/types/Conflicten';
import { Defect } from '@/types/Defecten';

import usePlankTableStore from '../../hooks/usePlankTableStore';
import ConflictIcon from '../conflict/ConflictParts/ConflictIcon';
import NotitieRelatedItem from '../Notitie/NotitieRelatedItem';
import ReserveIcon from '../reserve/ReserveIcon';
import SidepanelToggle from '../Sidepanel/Toggle/SidepanelToggle';

import DefectIcon from './DefectIcon';

import plankStyles from '../../PlankView.module.css';
import conflictStyles from '../conflict/Conflict.module.css';
import tableStyles from '../Table/Table.module.css';
import defectStyles from './Defect.module.css';

const DefectSidepanel: FC<{ defect: Defect; relatedConflict?: Conflict }> = ({
  defect,
  relatedConflict = undefined,
}) => {
  const nietMeerInzetbaarVanaf = usePlankTableStore(
    (state) => state.defectNietInzetbaarVanaf?.[defect.eenheidNummer] ?? null,
  );
  const isOnderhoudHerstel = usePlankTableStore((state) => state.sidepanel.isOnderhoud);
  const relatedReserve = usePlankTableStore((state) => state.reserveByStel[defect.eenheidNummer]);
  const relatedNotitie = usePlankTableStore((state) => state.getDefectNotitie(defect));
  const sidepanel = usePlankTableStore((state) => state.sidepanel);
  const defectenByStel = usePlankTableStore((state) => state.defectenByStel);

  const stelSnelheidBeperking =
    sidepanel.view === 'defect' && sidepanel.defect && !sidepanel.defect.snelheidBeperking
      ? (defectenByStel[String(sidepanel.defect.eenheidNummer)] ?? [])
          .filter((d) => d.snelheidBeperking)
          .sort((a, b) => parseSnelheid(a.snelheidBeperking!) - parseSnelheid(b.snelheidBeperking!))
          .at(0)
      : undefined;
  return (
    <div className={defectStyles.defectSidepanelCard}>
      <div className={defectStyles.defectSidepanelDiv}>
        <div className={defectStyles.defectSidepanelInnerDiv}>
          <div className={plankStyles.eenheidStyle}>{defect.eenheidNummer}</div>
          <DefectIcon
            defectSoort={defectTypeNietInzetbaar(defect) ? 'DEFECT_NIET_INZETBAAR' : defect.soort}
            nietInzetbaarBinnen24Uur={defectIsBinnenPeriodeNietInzetbaar({
              ...defect,
              nietMeerInzetbaarVanaf,
            })}
          />
          {isOnderhoudHerstel && defect?.werkplaats && (
            <div className={plankStyles.drglpStyle}>{defect?.werkplaats?.toLocaleLowerCase()}</div>
          )}
          <div className={plankStyles.defectToelichtingContainer}>
            <div className={`${plankStyles.toelichtingStyle} ${plankStyles.defectToelichtingText}`}>
              {defect.toelichting}
            </div>
            {(defect?.snelheidBeperking || stelSnelheidBeperking) && (
              <>
                {stelSnelheidBeperking && (
                  <DefectIcon
                    defectSoort={defectTypeNietInzetbaar(defect) ? 'BEPERKING_IN_TREINDIENST' : defect.soort}
                    nietInzetbaarBinnen24Uur={defectIsBinnenPeriodeNietInzetbaar({
                      ...defect,
                      nietMeerInzetbaarVanaf,
                    })}
                  />
                )}
                <span
                  className={
                    plankStyles.defectSnelheidBeperking +
                    (stelSnelheidBeperking ? ` ${plankStyles.defectSnelheidBeperkingWithIcon}` : '')
                  }
                >
                  {'Snelheid: '}
                  <span className={plankStyles.defectStandaardSnelheid}>
                    {defect?.standaardSnelheid ?? stelSnelheidBeperking?.standaardSnelheid}
                  </span>
                  {` ${defect?.snelheidBeperking ?? stelSnelheidBeperking?.snelheidBeperking}`}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      {relatedConflict && (
        <div className={tableStyles.relatedItemStyle}>
          <SidepanelToggle
            data-testid={`relatedConflict${defect.eenheidNummer}`}
            activeStyle={false}
            eenheid={relatedConflict?.eenheidNummer ?? 0}
            conflict={relatedConflict}
            className={conflictStyles.relatedDefectStyle}
          >
            <ConflictIcon
              conflictType={relatedConflict.type}
              className={conflictStyles.relatedConflictIconStyle}
              width='24px'
              height='24px'
            />
          </SidepanelToggle>
        </div>
      )}
      {relatedReserve && (
        <div className={tableStyles.relatedItemStyle}>
          <SidepanelToggle
            data-testid={`relatedReserve${relatedReserve.eenheid}`}
            activeStyle={false}
            eenheid={relatedReserve.eenheid}
            reserve={relatedReserve}
            className={conflictStyles.relatedReserveStyle}
          >
            <ReserveIcon />
          </SidepanelToggle>
        </div>
      )}
      <NotitieRelatedItem notitie={relatedNotitie} />
    </div>
  );
};
export default DefectSidepanel;
